/**
 * Contributors tab — PLT-4915.
 *
 * Single-valence list of contributing products for the selected impact
 * category. Rendered twice (once per valence) by the panel — the Positive
 * tab and the Negative tab are two instances of this component with
 * different `valence` props.
 *
 * Splitting the per-valence cards into their own tabs (Janne review v3,
 * May 2026) lets a click on the negative bar route directly to the
 * negative-contributors tab without an intermediate "show me both"
 * surface, and removes a layer of nested cards inside the panel body —
 * the tab strip is now the valence affordance, so we can drop the
 * per-card outline + pill and let the rows breathe.
 *
 * Data source is `useCompanyDrilldownContributions(modelResultId)` —
 * the same react-query hook used by the legacy `CompanyContributing
 * ProductList` info-box. It hits `/company/<resultId>/drilldown-
 * contributions` and returns a `Contributions` shape keyed by impact
 * × valence × productCode. We slice the top 4 here.
 *
 * Design notes (PLT-4915 Janne review v3):
 *   - No card outline / pill / count badge — the tab itself communicates
 *     valence (POSITIVE/NEGATIVE) and we already restate the magnitude
 *     in the header stat row. The previous nested "POSITIVE
 *     CONTRIBUTORS" pill inside a per-card outline was redundant chrome
 *     once each valence got its own tab.
 *   - Bars are normalised to the leader so the top contributor always
 *     fills 100% of the track — preserves visual rhythm even when the
 *     category total is dispersed across many small products.
 *   - Per-row right-aligned stack: `{share}%` (bold) on top,
 *     `{value} c/$` (muted) underneath. Answers the "13% of what?"
 *     question every reader had on the first cut.
 *   - "View all N contributors →" lilac link at the foot of the list
 *     (only when N > TOP_N) — the same drilldown route as the legacy
 *     info-box.
 */
import { Banner } from '@primer/react/experimental';
import { isProduction } from '@upright/common/environment';
import { Valence, ValueChain } from '@upright/common/model/types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { ModelEntityType } from '@upright/common/model/input/types';
import { Contributions, FinalScoreValence } from '@upright/common/model/output/scores/drilldowns/types';
import { ValueChainBreakdown } from '@upright/common/model/output/scores/drilldowns/leena2-top-impacts-types';
import {
  getValueChainFill,
  valueChainSegments,
} from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-chain-fill';
import { ErrorWithHttpStatus } from '@upright/ui/api/common/error-with-http-status';
import { reactQueryStatusToRequestStatus, RequestStatus } from '@upright/ui/api/common/types';
import { BouLink } from '@upright/ui/common/generic/content/texts/bou-link';
import { getModelEntityUrl } from '@upright/ui/untidy/urls';
import { useModelEntityDrilldownUrl } from '@upright/ui/untidy/urls';
import { useCompanyDrilldownContributions } from '@upright/ui/views/model-entity-views/company-view/drilldown/use-get-drilldown-contributions';
import { keyBy, mapValues } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { DrillInSectionHeader } from '../components/section-header';
import { AskWhyButton } from './ask-why-button';
import { drillInImpactCodes, isDimensionTarget, NetImpactDrillInPayload } from '../state';

interface Props {
  payload: NetImpactDrillInPayload;
  /** Which side of the chart this tab renders. */
  valence: Valence;
  /** Friendly display name of the impact category, e.g. "GHG emissions". */
  impactLabel: string;
  /** Friendly display name of the entity, e.g. "Siemens". */
  entityName: string;
  /** Whether to offer the "why" chat question about the top contributor: viewers who can chat. */
  showAskWhy: boolean;
}

interface ContributingProduct {
  productCode: string;
  productTitle: string | undefined;
  /** Raw contribution number from the drilldown (positive magnitude for both valences). */
  value: number;
  /** Per-value-chain split of `value`, merged across the panel's impact codes.
   *  Undefined when no contribution carried a breakdown (legacy / ANY-only) —
   *  the row then renders the plain valence bar. */
  valueChainBreakdown?: ValueChainBreakdown;
}

const TOP_N = 4;

export function ContributorsTab({ payload, valence, impactLabel, entityName, showAskWhy }: Props) {
  const drilldownQuery = useCompanyDrilldownContributions(payload.modelResultId);
  const getDrilldownUrl = useModelEntityDrilldownUrl();
  const valenceWord = valence === Valence.POSITIVE ? 'positive' : 'negative';
  // Janne review v5 promotes the v4 supporting subhead into a proper
  // section header — same component (`DrillInSectionHeader`) the
  // Everyday units tab uses for its per-valence blocks. The header
  // carries the title on the left and the per-valence c/$ figure on
  // the right (teal for positive, purple for negative). The "Top
  // products driving …" body sentence is gone because the tab strip
  // already says "Positive"/"Negative" and the product list directly
  // below the header is self-explanatory.
  const sectionTitle = `${entityName}'s ${valenceWord} ${impactLabel.toLowerCase()} impact`;

  const status = reactQueryStatusToRequestStatus({
    status: drilldownQuery.status,
    fetchStatus: drilldownQuery.fetchStatus,
  });

  const valenceCentsPerDollar =
    valence === Valence.POSITIVE ? payload.positiveCentsPerDollar : payload.negativeCentsPerDollar;

  if (payload.modelResultId === undefined) {
    return (
      <Empty>
        <EmptyTitle>No detailed product data</EmptyTitle>
        <EmptyBody>
          This entity doesn't have a published model result, so we can't break the impact down to contributing products.
          The cents-per-dollar totals above are still based on the full company score.
        </EmptyBody>
      </Empty>
    );
  }

  if (status === RequestStatus.LOADING) {
    return <SkeletonList valence={valence} sectionTitle={sectionTitle} valenceCentsPerDollar={valenceCentsPerDollar} />;
  }

  if (status === RequestStatus.ERROR || !drilldownQuery.data) {
    // A 410 (Gone) from the backend means this company's model result is on a
    // version that isn't loaded here — the norm in non-production environments,
    // whose database is a stripped snapshot holding only a subset of versions. Say
    // so specifically rather than the generic "couldn't load", and don't pretend a
    // refresh will help — the data simply isn't in this environment. Only outside
    // production: in prod a 410 wouldn't reflect a stripped DB, and the
    // "non-production environment" wording would be wrong — fall back to generic.
    const error = drilldownQuery.error;
    const isVersionGone = !isProduction() && error instanceof ErrorWithHttpStatus && error.status === 410;
    if (isVersionGone) {
      return (
        <VersionGoneWrap>
          <Banner
            variant="warning"
            title="Product-level data unavailable in this environment"
            description="Contributing products can't be shown because the product model version used to produce this company result is not available in this non-production environment. The cents-per-dollar totals above and the chat tab still work."
          />
        </VersionGoneWrap>
      );
    }
    return (
      <Empty>
        <EmptyTitle>Couldn't load contributing products</EmptyTitle>
        <EmptyBody>
          Try refreshing the page. If the issue persists, the cents-per-dollar totals above and the chat tab still work
          without product-level data.
        </EmptyBody>
      </Empty>
    );
  }

  const productNamesByCode = mapValues(
    keyBy(payload.productNames ?? [], d => d.id),
    d => d.name,
  );

  const ranked = rankProducts(drilldownQuery.data, drillInImpactCodes(payload.target), valence, productNamesByCode);
  const valenceTotal = totalAbsValue(ranked);

  // The "view all" drilldown route is keyed on a single impact code, so
  // it only applies at impact grain. At dimension grain we drop the link
  // (there is no single per-impact drilldown for a whole dimension).
  const isDimension = isDimensionTarget(payload.target);
  // We already early-returned above when `modelResultId` is undefined,
  // so it's guaranteed to be a string here.
  const drilldownHref =
    isDimension || payload.target.grain !== 'impact'
      ? '#'
      : (() => {
          try {
            return getDrilldownUrl({
              id: payload.entity.id,
              type: payload.entity.type,
              impactCode: payload.target.impactCode,
              valence,
              name: payload.entity.name,
              resultId: payload.modelResultId,
            });
          } catch {
            return '#';
          }
        })();

  const headerFigure = formatHeaderCentsPerDollar(valenceCentsPerDollar, valence);

  if (ranked.length === 0) {
    return (
      <Root>
        <DrillInSectionHeader title={sectionTitle} figure={headerFigure} figureValence={valence} />
        <NoProducts>No contributing products on the {valenceWord} side.</NoProducts>
      </Root>
    );
  }

  return (
    <Root>
      <DrillInSectionHeader title={sectionTitle} figure={headerFigure} figureValence={valence} />
      {/* Janne review v6 asked for a label above the product list so the
         reader knows what the rows below represent. The section header
         carries "Siemens's positive physical diseases impact" + total
         c/$ on the right; this eyebrow names the list itself. Same
         visual treatment as the chat tab's "OR TRY ONE OF THESE"
         eyebrow (uppercase, xs, gray-600). */}
      <ListEyebrow>Top contributing products</ListEyebrow>
      <ProductList>
        {ranked.slice(0, TOP_N).map((product, _idx, arr) => (
          <ProductRow
            key={product.productCode}
            product={product}
            leaderValue={arr[0]?.value ?? 0}
            valenceTotal={valenceTotal}
            valenceCentsPerDollar={valenceCentsPerDollar}
            valence={valence}
            productViewAccess={payload.hasProductViewAccess}
            showValueChains={payload.showValueChains}
          />
        ))}
      </ProductList>
      {ranked.length > TOP_N && !isDimension && (
        <ViewAllLink>
          <BouLink to={drilldownHref} target="_blank" rel="noreferrer">
            View all {ranked.length} {valenceWord} contributors →
          </BouLink>
        </ViewAllLink>
      )}
      {showAskWhy && (
        <AskWhyButton
          payload={payload}
          productName={ranked[0].productTitle || ranked[0].productCode}
          valence={valenceWord}
          impactLabel={impactLabel}
          entityName={entityName}
        />
      )}
    </Root>
  );
}

function rankProducts(
  contributions: Contributions,
  impactCodes: ImpactCategoryId[],
  valence: Valence,
  productNamesByCode: Record<string, string>,
): ContributingProduct[] {
  // Sum each product's contribution across all impact codes the panel
  // covers — a single code at impact grain, or the dimension's member
  // categories at dimension grain (PLT-4940). A product that contributes
  // to several categories in the dimension is ranked by its combined
  // magnitude.
  const valueByProduct = new Map<string, number>();
  // Per-product value-chain magnitudes, summed across the panel's impact codes.
  // A product keeps an entry here only if at least one of its contributions
  // carried a `valueChainBreakdown`; products with no genuine split stay absent
  // and fall back to the plain valence bar.
  const breakdownByProduct = new Map<string, ValueChainBreakdown>();
  for (const impact of impactCodes) {
    const byProduct = contributions[impact]?.[valence as FinalScoreValence] ?? {};
    for (const productCode of Object.keys(byProduct)) {
      const contribution = byProduct[productCode];
      const value = contribution.contribution || 0;
      if (value <= 0) continue;
      valueByProduct.set(productCode, (valueByProduct.get(productCode) ?? 0) + value);
      const breakdown = contribution.valueChainBreakdown;
      if (breakdown) {
        const merged = breakdownByProduct.get(productCode) ?? {};
        for (const vc of Object.keys(breakdown) as ValueChain[]) {
          merged[vc] = (merged[vc] ?? 0) + (breakdown[vc] ?? 0);
        }
        breakdownByProduct.set(productCode, merged);
      }
    }
  }
  const list: ContributingProduct[] = [...valueByProduct.entries()].map(([productCode, value]) => ({
    productCode,
    productTitle: productNamesByCode[productCode],
    value,
    valueChainBreakdown: breakdownByProduct.get(productCode),
  }));
  // Sort by absolute contribution descending — the backend reports
  // both valences as positive magnitudes so a plain numeric descending
  // sort gives us the correct ranking either way.
  return list.filter(p => p.value > 0).sort((a, b) => b.value - a.value);
}

function totalAbsValue(products: ContributingProduct[]): number {
  return products.reduce((acc, p) => acc + p.value, 0);
}

function ProductRow({
  product,
  leaderValue,
  valenceTotal,
  valenceCentsPerDollar,
  valence,
  productViewAccess,
  showValueChains,
}: {
  product: ContributingProduct;
  /** Top-of-valence contribution — used to normalise the bar to 100% at the leader. */
  leaderValue: number;
  /** Sum of all contributions in this valence — used to compute share-of-valence. */
  valenceTotal: number;
  /** Category-total c/$ for this valence — multiplied by share to get the row's absolute c/$. */
  valenceCentsPerDollar: number | null;
  valence: Valence;
  productViewAccess: boolean;
  /** When on (and the row has a genuine breakdown), the bar fill is split by value chain. */
  showValueChains: boolean;
}) {
  const sharePct = valenceTotal > 0 ? (product.value / valenceTotal) * 100 : 0;
  // Normalise the bar against the leader so the top contributor's bar
  // always fills 100% of the track — keeps the visual rhythm even
  // when the category total is dispersed across many small products.
  const barPct = leaderValue > 0 ? (product.value / leaderValue) * 100 : 0;
  // Per-row absolute c/$ = the row's share of this valence × the
  // category-total c/$. Approximate (the share is computed from the
  // top-N + tail) but close enough to be informative on the side panel.
  const rowCentsPerDollar = valenceCentsPerDollar ? (sharePct / 100) * valenceCentsPerDollar : null;
  const sharePctDisplay = sharePct < 10 ? sharePct.toFixed(1) : sharePct.toFixed(0);

  const label = product.productTitle || product.productCode;
  // Only split the fill when value-chain mode is on AND the row carries a
  // genuine breakdown (legacy / ANY-only rows return `[]` and keep the plain
  // valence bar). The bar's overall length (leader-normalised `barPct`) is
  // unchanged — only its internal fill is divided into value-chain segments.
  const segments = showValueChains ? valueChainSegments(product.valueChainBreakdown) : [];
  return (
    <ProductRowRoot>
      <ProductLabel title={label}>
        {productViewAccess ? (
          <BouLink to={getModelEntityUrl(ModelEntityType.PRODUCT, product.productCode, product.productTitle)}>
            {label}
          </BouLink>
        ) : (
          label
        )}
      </ProductLabel>
      <ProductBarWrap>
        {segments.length > 0 ? (
          <ProductBarStack style={{ width: `${Math.min(100, barPct)}%` }}>
            {segments.map(segment => (
              <ProductBarSegment
                key={segment.valueChain}
                style={{ width: `${segment.fraction * 100}%`, background: getValueChainFill(segment.valueChain) }}
              />
            ))}
          </ProductBarStack>
        ) : (
          <ProductBar style={{ width: `${Math.min(100, barPct)}%` }} $valence={valence} />
        )}
      </ProductBarWrap>
      <ProductFigures>
        <ProductPct>{sharePctDisplay}%</ProductPct>
        {rowCentsPerDollar !== null && <ProductCps>{formatCentsPerDollar(rowCentsPerDollar)}</ProductCps>}
      </ProductFigures>
    </ProductRowRoot>
  );
}

function formatCentsPerDollar(value: number): string {
  // Round to 2 sig figs for small values, 3 for larger ones.
  // Always format on the magnitude so an accidental negative input from
  // the backend can't produce mangled output like "−-3.0 c/$". Mirrors
  // `formatNet` in `panel.tsx`.
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  const rounded = abs < 0.1 ? abs.toPrecision(1) : abs.toPrecision(2);
  return `${sign}${rounded} c/$`;
}

/** Header figure: signed c/$ for the valence (e.g. "+1.27 c/$", "−2.5
 *  c/$"). Mirrors the formatter used by the Everyday units header so the
 *  three tabs read uniformly. */
export function formatHeaderCentsPerDollar(value: number | null, valence: Valence): string {
  if (value === null || Number.isNaN(value)) return '—';
  // Format on the magnitude — the sign comes from the valence. Without
  // `Math.abs(value)` a negative-magnitude payload (which the backend
  // shouldn't send today, but we don't enforce) would render as
  // "+-3.0 c/$".
  const abs = Math.abs(value);
  const sign = valence === Valence.POSITIVE ? '+' : '−';
  const rounded = abs < 1 ? abs.toPrecision(1) : abs.toPrecision(3);
  return `${sign}${rounded} c/$`;
}

/* ─── Skeleton list for loading state ────────────────────────────────── */

function SkeletonList({
  valence,
  sectionTitle,
  valenceCentsPerDollar,
}: {
  valence: Valence;
  sectionTitle: string;
  valenceCentsPerDollar: number | null;
}) {
  return (
    <Root>
      <DrillInSectionHeader
        title={sectionTitle}
        figure={formatHeaderCentsPerDollar(valenceCentsPerDollar, valence)}
        figureValence={valence}
      />
      <ListEyebrow>Top contributing products</ListEyebrow>
      <ProductList>
        {[0, 1, 2, 3].map(i => (
          <ProductRowRoot key={i}>
            <ProductLabel>
              <Skeleton width={140} height={12} />
            </ProductLabel>
            <ProductBarWrap>
              <Skeleton width="100%" height={6} />
            </ProductBarWrap>
            <ProductFigures>
              <Skeleton width={32} height={12} />
            </ProductFigures>
          </ProductRowRoot>
        ))}
      </ProductList>
      {/* Tag the skeleton with the valence for analytics-time inspection
         (e.g. screenshot tests assert the right tab rendered). */}
      <span aria-hidden="true" data-valence={valence} />
    </Root>
  );
}

/* ─── styled ─────────────────────────────────────────────────────────── */

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
`;

/* Subhead removed in Janne review v5 — the supporting "Top products
   driving …" sentence is gone now that the section header (provided by
   `DrillInSectionHeader`) sits at the top of the list with the valence
   c/$ figure on the right. The tab strip already names the valence and
   the product list directly under the header is self-explanatory. */

/* List eyebrow — Janne review v6 asked for a header above the product
   rows so the reader knows what the list represents (the section
   header above it is about the company-level impact total + c/$;
   without this eyebrow it's not obvious that the rows below are the
   contributing products). Tiny gray-600 uppercase, same treatment as
   the chat tab's "OR TRY ONE OF THESE" eyebrow so the panel reads
   uniformly. Janne review v7 asked for more breathing room on both
   sides — add `--space__sm` margin top and bottom on top of the Root's
   `--space__md` flex gap, so the eyebrow reads as a list header rather
   than a label glued to the section header above or the first row
   below. */
const ListEyebrow = styled.div`
  margin-top: var(--space__sm);
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  letter-spacing: 0.04em;
  color: var(--color__gray-600);
  text-transform: uppercase;
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
`;

const ProductRowRoot = styled.div`
  display: grid;
  /* Three columns: name | bar | right-aligned figures stack. Bar gets a
     comfortable 80px min so a long label can still wrap before the bar
     gets squashed. */
  grid-template-columns: minmax(0, 1fr) minmax(80px, 30%) 64px;
  align-items: center;
  gap: var(--space__sm);
  font-size: var(--font-size__sm);
`;

const ProductLabel = styled.div`
  color: var(--color__gray-900);
  font-size: var(--font-size__sm);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ProductBarWrap = styled.div`
  width: 100%;
  height: 6px;
  background: var(--color__gray-100);
  border-radius: var(--border-radius__full);
  overflow: hidden;
`;

const ProductBar = styled.div<{ $valence: Valence }>`
  height: 100%;
  background: ${p => (p.$valence === Valence.POSITIVE ? 'var(--color__teal-700)' : 'var(--color__purple-700)')};
  border-radius: inherit;
`;

/* Value-chain split fill (PLT-5382): the outer width is the same leader-
   normalised bar length as `ProductBar`; the inner segments divide that
   length by value-chain magnitude. Each segment is colored via
   `getValueChainFill` (same palette as the main impact chart). */
const ProductBarStack = styled.div`
  display: flex;
  height: 100%;
  border-radius: inherit;
  overflow: hidden;
`;

const ProductBarSegment = styled.div`
  height: 100%;
`;

const ProductFigures = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
`;

const ProductPct = styled.div`
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
`;

const ProductCps = styled.div`
  font-size: var(--font-size__xs);
  color: var(--color__gray-700);
  margin-top: 1px;
`;

const ViewAllLink = styled.div`
  margin-top: var(--space__sm);
  font-size: var(--font-size__sm);
  a {
    color: var(--color__gray-900);
    text-decoration: none;
    font-weight: var(--font-weight__medium);
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Empty = styled.div`
  padding: var(--space__lg) 0;
`;

/* Version-unavailable banner sits closer to the top of the panel body than the
   other empty states — the Banner already carries its own internal padding, so we
   only keep a little breathing room below. */
const VersionGoneWrap = styled.div`
  padding: var(--space__sm) 0 var(--space__lg);
`;

const EmptyTitle = styled.div`
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
  margin-bottom: 4px;
`;

const EmptyBody = styled.div`
  font-size: var(--font-size__sm);
  color: var(--color__gray-700);
  line-height: 1.5;
`;

const NoProducts = styled.div`
  font-size: var(--font-size__sm);
  color: var(--color__gray-700);
  padding: var(--space__sm) 0;
`;
